import { useEffect, useState } from 'react';
import { api, getSession } from '../api/client.ts';
import { ligneRmaCpnAPlat, totalPartie3, avertissementsControle } from '../../../shared/business-logic/index.ts';
import type { RmaCpnResultat } from '../../../shared/business-logic/cpn.ts';
import type { Periode } from './Accueil.tsx';

export default function Rma({ periode, onPeriodeChange }: { periode: Periode | null; onPeriodeChange: (p: Periode) => void }) {
  const [resultat, setResultat] = useState<any>(null);
  const [resultatCpn, setResultatCpn] = useState<RmaCpnResultat | null>(null);
  const [chargement, setChargement] = useState(false);
  const session = getSession();

  useEffect(() => {
    if (periode) charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periode?.id]);

  async function charger() {
    if (!periode) return;
    setChargement(true);
    try {
      const [r, rCpn] = await Promise.all([
        api.get(`/rma/${periode.id}`),
        api.get<{ resultat: RmaCpnResultat }>(`/cpn/rma/${periode.id}`),
      ]);
      setResultat(r);
      setResultatCpn(rCpn.resultat);
    } finally {
      setChargement(false);
    }
  }

  async function cloturer() {
    if (!periode) return;
    if (!confirm('Êtes-vous sûr de vouloir clôturer ce mois ? Les données seront verrouillées.')) return;
    const maj = await api.post<Periode>(`/rma/${periode.id}/cloturer`, {});
    onPeriodeChange({ ...periode, statut: 'CLOTURE' });
  }

  if (!periode) return <p>Sélectionnez d'abord une période sur l'écran Accueil.</p>;
  if (chargement || !resultat) return <p>Calcul du RMA en cours…</p>;

  return (
    <div>
      <h1>RMA — {periode.mois}/{periode.annee}</h1>
      <p>Statut de la période : <strong>{periode.statut}</strong></p>
      {resultat.controle && avertissementsControle(resultat.controle).length > 0 && (
        <div className="messages-erreur" role="alert">
          <strong>Attention — lignes non comptées dans ce RMA (corrigez-les dans Saisie) :</strong>
          <ul>{avertissementsControle(resultat.controle).map((a: string, i: number) => <li key={i}>{a}</li>)}</ul>
        </div>
      )}

      <h2>Partie 1 — Diagnostics / Symptômes</h2>
      <table>
        <thead><tr><th>Diagnostic</th><th>Total</th></tr></thead>
        <tbody>
          {resultat.partie1.lignes.filter((l: any) => l.total > 0).map((l: any) => (
            <tr key={l.diagnostic}><td>{l.diagnostic}</td><td>{l.total}</td></tr>
          ))}
        </tbody>
      </table>

      <h2>Partie 3 — Paludisme</h2>
      <table>
        <thead><tr><th>Tranche</th><th>Fièvre</th><th>TDR fait</th><th>101</th><th>110</th><th>111</th><th>ACT</th><th>Artesunate</th></tr></thead>
        <tbody>
          {resultat.partie3.map((l: any) => (
            <tr key={l.tranche}>
              <td>{l.tranche}</td><td>{l.casFievre}</td><td>{l.tdrEffectue}</td>
              <td>{l.positif101}</td><td>{l.positif110}</td><td>{l.positif111}</td>
              <td>{l.traitesACT}</td><td>{l.traitesArtesunate}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          {(() => {
            const t = totalPartie3(resultat.partie3);
            return (
              <tr style={{ fontWeight: 700, background: '#e8eef7' }}>
                <td>Total</td><td>{t.casFievre}</td><td>{t.tdrEffectue}</td>
                <td>{t.positif101}</td><td>{t.positif110}</td><td>{t.positif111}</td>
                <td>{t.traitesACT}</td><td>{t.traitesArtesunate}</td>
              </tr>
            );
          })()}
        </tfoot>
      </table>

      <h2>Partie 6 — CSAJ</h2>
      <table>
        <thead><tr><th></th><th>10-14 ans</th><th>15-19 ans</th><th>20-24 ans</th></tr></thead>
        <tbody>
          <tr><td>IST</td><td>{resultat.partie6.ist['10-14']}</td><td>{resultat.partie6.ist['15-19']}</td><td>{resultat.partie6.ist['20-24']}</td></tr>
          <tr><td>CE</td><td>{resultat.partie6.ce['10-14']}</td><td>{resultat.partie6.ce['15-19']}</td><td>{resultat.partie6.ce['20-24']}</td></tr>
        </tbody>
      </table>

      {resultatCpn && (
        <>
          <h2>RMA CPN</h2>
          <table>
            <thead><tr><th>Indicateur</th><th>Effectif</th></tr></thead>
            <tbody>
              {ligneRmaCpnAPlat(resultatCpn).map((l, i) =>
                'section' in l
                  ? <tr key={i}><td colSpan={2} style={{ fontWeight: 700, background: '#f8d7e6' }}>{l.section}</td></tr>
                  : <tr key={i}><td style={{ textAlign: 'left' }}>{l.label}</td><td>{l.valeur}</td></tr>
              )}
            </tbody>
          </table>
        </>
      )}

      {periode.statut !== 'CLOTURE' && (session.role === 'RESPONSABLE_CSB' || session.role === 'ADMIN') && (
        <div style={{ marginTop: 24 }}>
          <button className="danger" onClick={cloturer}>Clôturer le mois</button>
        </div>
      )}
    </div>
  );
}
